import React, { useMemo, useState } from 'react'
import { useTable } from '@refinedev/antd'
import type { CrudFilters } from '@refinedev/core'
import { App, Button, Input, Popconfirm, Result, Select, Space, Spin, Table, Typography } from 'antd'
import type { Tables } from '@muaa/shared'
import { supabaseClient } from '../../lib/supabaseClient'
import { ListPage, SectionCard, StatStrip, TableSurface, EmptyHint, type Stat } from '../../theme/listkit'
import { brand } from '../../theme/brand'
import { ShieldCheck, UserCheck, Ban, UsersRound } from '../../components/icons'
import { fnErrorMessage } from '../../lib/fnError'

type Profile = Tables<'profiles'>

/** Every integrity lever the `admin-action` Edge Function supports (the same set
 *  the per-member Integrity actions card uses), each with a one-line effect. */
interface ActionDef {
  value: string
  label: string
  danger?: boolean
  effect: string
}
const ACTIONS: ActionDef[] = [
  { value: 'suspend', label: 'Suspend (account ban)', danger: true, effect: 'Bans the account — the member is signed out and cannot sign back in until unsuspended.' },
  { value: 'unsuspend', label: 'Unsuspend (restore account)', effect: 'Lifts an account suspension and restores normal access.' },
  { value: 'stream_ban', label: 'Ban from chat', danger: true, effect: 'Bans the member in chat — they can no longer send messages in any channel.' },
  { value: 'stream_shadow_ban', label: 'Shadow-ban (chat)', effect: 'Silently bans in chat — their messages are hidden from everyone but themselves; they are not told.' },
  { value: 'stream_deactivate', label: 'Deactivate chat', danger: true, effect: 'Removes the member’s chat user entirely — they drop out of every channel and cannot connect.' },
  { value: 'stream_reactivate', label: 'Reactivate chat', effect: 'Restores a previously-deactivated chat user.' },
]
const ACTION_BY_VALUE = new Map(ACTIONS.map((a) => [a.value, a]))

const CONCURRENCY = 5 // max in-flight admin-action invokes — easy on the function.

interface BulkError {
  userId: string
  name: string
  error: string
}
interface BulkResult {
  action: string
  total: number
  applied: number
  errors: BulkError[]
}

/**
 * Integrity Actions — apply any integrity lever to MANY members at once. Pick
 * members from the searchable / filterable roster (multi-select), choose an
 * action, and confirm; each selected member is run through the admin-gated
 * `admin-action` Edge Function (the same one the per-member card uses), throttled
 * to a small concurrency, with a per-member result summary.
 *
 * The roster is a PAGINATED `useTable`: search/class/role are pushed down as
 * server-side filters (name contains, class_year eq, role eq) so we only ever
 * load one page of profiles — never the whole roster — and the main thread never
 * scans the full table.
 */
export function IntegrityActionsPage() {
  const { message } = App.useApp()
  const [search, setSearch] = useState('')
  const [classFilter, setClassFilter] = useState<string | undefined>(undefined)
  const [roleFilter, setRoleFilter] = useState<string | undefined>(undefined)
  const [selected, setSelected] = useState<string[]>([])
  const [action, setAction] = useState<string | undefined>(undefined)
  const [applying, setApplying] = useState(false)
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null)

  // Push search + filters to the server. Recomputed on every keystroke/selection.
  const filters = useMemo<CrudFilters>(() => {
    const f: CrudFilters = []
    const q = search.trim()
    if (q) f.push({ field: 'name', operator: 'contains', value: q })
    if (classFilter) f.push({ field: 'class_year', operator: 'eq', value: classFilter })
    if (roleFilter) f.push({ field: 'role', operator: 'eq', value: roleFilter })
    return f
  }, [search, classFilter, roleFilter])

  const { tableProps, setFilters } = useTable<Profile>({
    resource: 'profiles',
    sorters: { initial: [{ field: 'name', order: 'asc' }] },
    pagination: { pageSize: 25 },
    syncWithLocation: false,
  })

  // Apply the derived filters to the server-side query.
  React.useEffect(() => {
    setFilters(filters, 'replace')
  }, [filters, setFilters])

  // The current page of profiles (already bounded by the server query).
  const filtered = useMemo(
    () => ((tableProps.dataSource ?? []) as Profile[]).filter((m) => !!m.user_id),
    [tableProps.dataSource],
  )

  // Name lookup scoped to the rows on screen — used to label any bulk errors.
  const byId = useMemo(() => {
    const m = new Map<string, Profile>()
    for (const p of filtered) if (p.user_id) m.set(p.user_id, p)
    return m
  }, [filtered])

  // Dropdown options derive from the current page's rows (bounded, not the whole
  // table); they refine as the roster is paged/filtered.
  const classYears = useMemo(
    () =>
      Array.from(new Set(filtered.map((m) => m.class_year).filter((c): c is string => !!c))).sort(),
    [filtered],
  )
  const roles = useMemo(
    () => Array.from(new Set(filtered.map((m) => String(m.role)).filter((r) => r && r !== 'null'))).sort(),
    [filtered],
  )

  const totalMembers = tableProps.pagination
    ? (tableProps.pagination as { total?: number }).total ?? filtered.length
    : filtered.length

  const selectedAction = action ? ACTION_BY_VALUE.get(action) : undefined
  const canApply = !!action && selected.length > 0 && !applying

  const onApply = async () => {
    if (!action || selected.length === 0) return
    setApplying(true)
    setBulkResult(null)
    const ids = [...selected]
    const errors: BulkError[] = []
    let applied = 0
    let cursor = 0
    const worker = async () => {
      while (cursor < ids.length) {
        const uid = ids[cursor++]
        try {
          const { error } = await supabaseClient.functions.invoke('admin-action', {
            body: { targetUserId: uid, action },
          })
          if (error) throw error
          applied += 1
        } catch (e) {
          errors.push({ userId: uid, name: byId.get(uid)?.name ?? uid, error: await fnErrorMessage(e) })
        }
      }
    }
    try {
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, ids.length) }, worker))
      setBulkResult({ action, total: ids.length, applied, errors })
      if (errors.length > 0) {
        message.warning(`Applied to ${applied} of ${ids.length} — ${errors.length} failed`)
      } else {
        message.success(`Applied "${selectedAction?.label ?? action}" to ${applied} member${applied === 1 ? '' : 's'}`)
        setSelected([])
      }
    } finally {
      setApplying(false)
    }
  }

  const stats: Stat[] = [
    { label: 'Members', value: totalMembers.toLocaleString(), icon: UsersRound, tone: 'jaguar' },
    { label: 'Shown', value: filtered.length.toLocaleString(), icon: ShieldCheck, tone: 'gold' },
    { label: 'Selected', value: selected.length.toLocaleString(), icon: UserCheck, tone: 'green' },
  ]

  const errCount = bulkResult?.errors.length ?? 0
  const resultStats: Stat[] = bulkResult
    ? [
        { label: 'Applied', value: bulkResult.applied, icon: UserCheck, tone: 'green' },
        { label: 'Failed', value: errCount, icon: Ban, tone: errCount > 0 ? 'gold' : 'neutral' },
        { label: 'Selected', value: bulkResult.total, icon: UsersRound, tone: 'jaguar' },
      ]
    : []

  return (
    <ListPage
      intro={{
        eyebrow: 'Trust & Safety · Integrity Actions',
        description:
          'Apply an integrity action to many members at once. Search and filter the roster, select one or more members, choose an action, and confirm — each runs through the admin-gated action service.',
      }}
      stats={stats}
    >
      <SectionCard
        title="Apply an action"
        sub="Pick an action, then confirm to run it on every selected member."
        style={{ marginBottom: 18 }}
      >
        <Space wrap align="center" style={{ rowGap: 10 }}>
          <Select
            data-testid="integrity-action-select"
            placeholder="Choose an integrity action"
            style={{ minWidth: 280 }}
            value={action}
            onChange={(v) => setAction(v)}
            options={ACTIONS.map((a) => ({ value: a.value, label: a.label }))}
          />
          <Popconfirm
            title={`Apply "${selectedAction?.label ?? ''}" to ${selected.length} member${selected.length === 1 ? '' : 's'}?`}
            description={selectedAction?.effect}
            okText="Apply"
            okButtonProps={{ danger: selectedAction?.danger, loading: applying }}
            cancelText="Cancel"
            onConfirm={onApply}
            disabled={!canApply}
          >
            <Button type="primary" danger={selectedAction?.danger} disabled={!canApply} loading={applying}>
              Apply to {selected.length} selected
            </Button>
          </Popconfirm>
        </Space>

        {selectedAction && (
          <Typography.Paragraph
            type="secondary"
            style={{ fontFamily: brand.fSans, fontSize: 12.5, lineHeight: 1.6, marginTop: 12, marginBottom: 0 }}
          >
            {selectedAction.effect}
          </Typography.Paragraph>
        )}

        {applying && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, fontFamily: brand.fSans, fontSize: 13, color: brand.sub }}>
            <Spin size="small" />
            <span>Applying to {selected.length} member(s)…</span>
          </div>
        )}

        {bulkResult && !applying && (
          <div style={{ marginTop: 20 }} data-testid="integrity-result">
            <StatStrip stats={resultStats} columns={3} />
            <Result
              status={errCount > 0 ? 'warning' : 'success'}
              title={errCount > 0 ? `Applied to ${bulkResult.applied} of ${bulkResult.total} — ${errCount} failed` : 'Action applied'}
              subTitle={`${ACTION_BY_VALUE.get(bulkResult.action)?.label ?? bulkResult.action} · ${bulkResult.applied} of ${bulkResult.total} member${bulkResult.total === 1 ? '' : 's'}`}
              style={{ paddingTop: 8 }}
            />
            {errCount > 0 && (
              <div data-testid="integrity-errors" style={{ marginTop: 4, border: '1px solid ' + brand.cardLine, borderRadius: 12, overflow: 'hidden' }}>
                <div style={{ fontFamily: brand.fSans, fontWeight: 700, fontSize: 11.5, letterSpacing: '.04em', textTransform: 'uppercase', color: brand.sub, background: brand.paper, padding: '10px 14px' }}>
                  Errors ({errCount})
                </div>
                {bulkResult.errors.map((e, i) => (
                  <div key={`${e.userId}-${i}`} style={{ display: 'flex', gap: 12, padding: '10px 14px', borderTop: i === 0 ? 'none' : '1px solid ' + brand.cardLine, fontFamily: brand.fSans, fontSize: 12.5 }}>
                    <span style={{ color: brand.ink, fontWeight: 600, flexShrink: 0 }}>{e.name}</span>
                    <span style={{ color: '#a4452f' }}>{e.error}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </SectionCard>

      <SectionCard title="Members" sub="Search and filter, then select one or many">
        <Space wrap style={{ marginBottom: 14, rowGap: 10 }}>
          <Input.Search
            data-testid="integrity-search"
            placeholder="Search by name, email, or id"
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 280 }}
          />
          <Select
            data-testid="integrity-class-filter"
            placeholder="Class year"
            allowClear
            value={classFilter}
            onChange={(v) => setClassFilter(v)}
            style={{ width: 150 }}
            options={classYears.map((c) => ({ value: c, label: `Class of ${c}` }))}
          />
          <Select
            data-testid="integrity-role-filter"
            placeholder="Role"
            allowClear
            value={roleFilter}
            onChange={(v) => setRoleFilter(v)}
            style={{ width: 140 }}
            options={roles.map((r) => ({ value: r, label: r }))}
          />
        </Space>

        {filtered.length === 0 ? (
          <EmptyHint>No members found.</EmptyHint>
        ) : (
          <TableSurface>
            <Table<Profile>
              {...tableProps}
              dataSource={filtered}
              rowKey="user_id"
              size="middle"
              pagination={
                tableProps.pagination
                  ? { ...tableProps.pagination, showSizeChanger: true }
                  : { pageSize: 25, showSizeChanger: true }
              }
              rowSelection={{
                selectedRowKeys: selected,
                preserveSelectedRowKeys: true,
                onChange: (keys) => setSelected(keys as string[]),
              }}
              columns={[
                {
                  title: 'Member',
                  dataIndex: 'name',
                  key: 'name',
                  render: (_: unknown, p: Profile) => (
                    <div>
                      <div style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 12.5, color: brand.ink }}>
                        {p.name || 'Unknown member'}
                      </div>
                      <code style={{ fontSize: 11, color: brand.sub }}>{p.user_id}</code>
                    </div>
                  ),
                },
                {
                  title: 'Class',
                  dataIndex: 'class_year',
                  key: 'class_year',
                  render: (v: string | null) => (v ? `Class of ${v}` : '—'),
                },
                {
                  title: 'Role',
                  dataIndex: 'role',
                  key: 'role',
                  render: (v: unknown) => String(v ?? '—'),
                },
              ]}
            />
          </TableSurface>
        )}
      </SectionCard>
    </ListPage>
  )
}
